import { HttpResponse } from 'msw';

import appConfig from '@/config';
import { typedHttp, worker } from '@/mocks';
import type {
  RecordFieldType,
  RecordPaginationType,
  RecordSectionParamType,
} from '@/types/individual_record';

/**
 * Mocks `GET /v0.3/records/{source_id}/{source_primary_key}` against the
 * contract in PEX-490 (Backend plan → Schema) until PEX-503 ships the route.
 * @see https://linear.app/xbeam/issue/PEX-490/irp-redesign-tech-plan (2026-09-08)
 */

type MockPartnerType = {
  partner_organization_uuid: string;
  partner_organization_id: number;
  partner_name: string;
  partner_tag_ids: number[];
  partner_score: string;
  partner_score_bucket: string;
  shared_with_us_population_ids: number[];
  shared_by_us_population_ids: number[];
  shares_master: boolean;
  shares_opportunities: boolean;
  shares_contacts: boolean;
};

function domainFor(partnerName: string) {
  return `${partnerName.toLowerCase().replace(/\s+/g, '')}.com`;
}

function field(
  name: string,
  column: string,
  value: RecordFieldType['value'],
  dataType: RecordFieldType['data_type'],
  isOurs: boolean,
  extras: Partial<RecordFieldType> = {},
): RecordFieldType {
  return {
    name,
    column,
    value,
    data_type: dataType,
    is_ours: isOurs,
    ...extras,
  };
}

const mockPartners: MockPartnerType[] = [
  {
    partner_organization_uuid: '11111111-1111-1111-1111-111111111111',
    partner_organization_id: 5001,
    partner_name: 'Acme Analytics',
    partner_tag_ids: [11, 12],
    partner_score: 'excellent',
    partner_score_bucket: 'high',
    shared_with_us_population_ids: [9101, 9102],
    shared_by_us_population_ids: [301],
    shares_master: true,
    shares_opportunities: true,
    shares_contacts: true,
  },
  {
    partner_organization_uuid: '22222222-2222-2222-2222-222222222222',
    partner_organization_id: 5002,
    partner_name: 'Bluewave Cloud',
    partner_tag_ids: [12],
    partner_score: 'strong',
    partner_score_bucket: 'high',
    shared_with_us_population_ids: [9201],
    shared_by_us_population_ids: [301, 302],
    shares_master: true,
    shares_opportunities: true,
    shares_contacts: false,
  },
  {
    partner_organization_uuid: '33333333-3333-3333-3333-333333333333',
    partner_organization_id: 5003,
    partner_name: 'Cinder Security',
    partner_tag_ids: [13],
    partner_score: 'moderate',
    partner_score_bucket: 'medium',
    shared_with_us_population_ids: [9301, 9302, 9303],
    shared_by_us_population_ids: [302],
    shares_master: true,
    shares_opportunities: false,
    shares_contacts: true,
  },
  {
    partner_organization_uuid: '44444444-4444-4444-4444-444444444444',
    partner_organization_id: 5004,
    partner_name: 'Delta Ledger',
    partner_tag_ids: [11],
    partner_score: 'medium',
    partner_score_bucket: 'medium',
    shared_with_us_population_ids: [9401],
    shared_by_us_population_ids: [],
    shares_master: true,
    shares_opportunities: false,
    shares_contacts: false,
  },
  {
    partner_organization_uuid: '55555555-5555-5555-5555-555555555555',
    partner_organization_id: 5005,
    partner_name: 'Everline Logistics',
    partner_tag_ids: [13, 14],
    partner_score: 'low',
    partner_score_bucket: 'low',
    shared_with_us_population_ids: [9501],
    shared_by_us_population_ids: [301],
    shares_master: false,
    shares_opportunities: true,
    shares_contacts: true,
  },
  {
    partner_organization_uuid: '66666666-6666-6666-6666-666666666666',
    partner_organization_id: 5006,
    partner_name: 'Foundry Labs',
    partner_tag_ids: [14],
    partner_score: 'weak',
    partner_score_bucket: 'low',
    shared_with_us_population_ids: [9601, 9602],
    shared_by_us_population_ids: [303],
    shares_master: false,
    shares_opportunities: false,
    shares_contacts: true,
  },
  {
    partner_organization_uuid: '77777777-7777-7777-7777-777777777777',
    partner_organization_id: 5007,
    partner_name: 'Granite Payments',
    partner_tag_ids: [],
    partner_score: 'unknown',
    partner_score_bucket: 'low',
    shared_with_us_population_ids: [9701],
    shared_by_us_population_ids: [],
    shares_master: true,
    shares_opportunities: true,
    shares_contacts: true,
  },
];

const ownFields: RecordFieldType[] = [
  field('Account Name', 'name', 'Northstar Robotics', 'string', true, {
    mdm_property: 'name',
    source_field_id: 71001,
  }),
  field('Website', 'website', 'northstarrobotics.com', 'string', true, {
    mdm_property: 'domain',
    source_field_id: 71002,
  }),
  field('Industry', 'industry', 'Manufacturing', 'string', true, {
    source_field_id: 71003,
  }),
  field('Annual Revenue', 'annual_revenue', 48000000, 'number', true, {
    source_field_id: 71004,
  }),
  field('Employees', 'number_of_employees', 1200, 'number', true, {
    source_field_id: 71005,
  }),
  field('Customer', 'is_customer', true, 'boolean', true, {
    source_field_id: 71006,
  }),
  field(
    'Created Date',
    'created_date',
    '2022-03-14T09:12:00Z',
    'datetime',
    true,
    {
      source_field_id: 71007,
    },
  ),
];

function partnerMasterFields(partner: MockPartnerType): RecordFieldType[] {
  return [
    field('Account Name', 'name', 'Northstar Robotics', 'string', false, {
      mdm_property: 'name',
      partner_population_ids: partner.shared_with_us_population_ids,
    }),
    field('Account Type', 'type', 'Customer', 'string', false, {
      partner_population_ids: partner.shared_with_us_population_ids,
    }),
    field(
      'Last Activity',
      'last_activity_date',
      '2026-08-21T15:04:00Z',
      'datetime',
      false,
      { partner_population_ids: partner.shared_with_us_population_ids },
    ),
  ];
}

function accountOwnerItems(name: string, email: string, isOurs: boolean) {
  return {
    items: [
      {
        fields: [
          field('Owner Name', 'full_name', name, 'string', isOurs, {
            mdm_property: 'full_name',
          }),
          field('Owner Email', 'email', email, 'string', isOurs, {
            mdm_property: 'email',
          }),
        ],
      },
    ],
  };
}

function opportunityItems(partnerName: string | null) {
  const isOurs = !partnerName;
  const prefix = partnerName ?? 'Northstar';
  return {
    items: [
      {
        fields: [
          field(
            'Opportunity Name',
            'name',
            `${prefix} — Platform Expansion`,
            'string',
            isOurs,
            {
              mdm_property: 'name',
            },
          ),
          field('Stage', 'stage_name', 'Negotiation', 'string', isOurs, {
            mdm_property: 'stage',
          }),
          field('Amount', 'amount', 185000, 'number', isOurs, {
            mdm_property: 'amount',
          }),
          field(
            'Close Date',
            'close_date',
            '2026-11-30T00:00:00Z',
            'datetime',
            isOurs,
            {
              mdm_property: 'close_date',
            },
          ),
        ],
      },
      {
        fields: [
          field(
            'Opportunity Name',
            'name',
            `${prefix} — Renewal FY27`,
            'string',
            isOurs,
            {
              mdm_property: 'name',
            },
          ),
          field('Stage', 'stage_name', 'Prospecting', 'string', isOurs, {
            mdm_property: 'stage',
          }),
          field('Amount', 'amount', 62000, 'number', isOurs, {
            mdm_property: 'amount',
          }),
          field(
            'Close Date',
            'close_date',
            '2027-02-14T00:00:00Z',
            'datetime',
            isOurs,
            {
              mdm_property: 'close_date',
            },
          ),
        ],
      },
    ],
  };
}

function productItems(isOurs: boolean) {
  return {
    items: [
      {
        fields: [
          field('Product Name', 'name', 'Core Platform', 'string', isOurs, {
            mdm_property: 'product_name',
          }),
          field('Product Key', 'product_code', 'CORE-001', 'string', isOurs, {
            mdm_property: 'product_key',
          }),
        ],
      },
    ],
  };
}

function dealProductItems(isOurs: boolean) {
  return {
    items: [
      {
        fields: [
          field('Quantity', 'quantity', 12, 'number', isOurs),
          field('Total Price', 'total_price', 144000, 'number', isOurs),
        ],
      },
    ],
  };
}

function contactItems(partnerName: string | null) {
  const isOurs = !partnerName;
  const domain = 'northstarrobotics.com';
  return {
    items: [
      {
        fields: [
          field('Name', 'full_name', 'Priya Raman', 'string', isOurs, {
            mdm_property: 'full_name',
          }),
          field('Title', 'title', 'VP Operations', 'string', isOurs, {
            mdm_property: 'title',
          }),
          field('Email', 'email', `priya.raman@${domain}`, 'string', isOurs, {
            mdm_property: 'email',
          }),
        ],
      },
      {
        fields: [
          field('Name', 'full_name', 'Marcus Hale', 'string', isOurs, {
            mdm_property: 'full_name',
          }),
          field('Title', 'title', 'Director of IT', 'string', isOurs, {
            mdm_property: 'title',
          }),
          field('Email', 'email', `marcus.hale@${domain}`, 'string', isOurs, {
            mdm_property: 'email',
          }),
        ],
      },
    ],
  };
}

function buildOwn(section: RecordSectionParamType) {
  const master = {
    fields: ownFields,
    account_owners: accountOwnerItems(
      'Dana Whitfield',
      'dwhitfield@example.com',
      true,
    ),
  };
  const opportunities = {
    opportunities: opportunityItems(null),
    products: productItems(true),
    deal_products: dealProductItems(true),
  };
  const contacts = { contacts: contactItems(null) };

  if (section === 'master') return master;
  if (section === 'opportunities') return opportunities;
  if (section === 'contacts') return contacts;
  return { ...master, ...opportunities, ...contacts };
}

function buildPartner(
  partner: MockPartnerType,
  section: RecordSectionParamType,
) {
  const {
    partner_organization_uuid,
    partner_name,
    shared_with_us_population_ids,
    shared_by_us_population_ids,
  } = partner;
  const base = {
    partner_organization_uuid,
    partner_name,
    shared_with_us_population_ids,
    shared_by_us_population_ids,
  };
  const master = {
    fields: partnerMasterFields(partner),
    account_owners: accountOwnerItems(
      `${partner_name} AE`,
      `ae@${domainFor(partner_name)}`,
      false,
    ),
  };
  const opportunities = {
    opportunities: opportunityItems(partner_name),
    products: productItems(false),
    deal_products: dealProductItems(false),
  };
  const contacts = { contacts: contactItems(partner_name) };

  if (section === 'master') return { ...base, ...master };
  if (section === 'opportunities') return { ...base, ...opportunities };
  if (section === 'contacts') return { ...base, ...contacts };
  return { ...base, ...master, ...opportunities, ...contacts };
}

/* Section membership is inclusive: a partner is in a section when it shares
   any of that section's collections. With no section the caller gets every
   collection, so a partner sharing anything belongs there. */
function sharesSection(
  partner: MockPartnerType,
  section: RecordSectionParamType,
) {
  if (section === 'master') return partner.shares_master;
  if (section === 'opportunities') return partner.shares_opportunities;
  if (section === 'contacts') return partner.shares_contacts;
  return (
    partner.shares_master ||
    partner.shares_opportunities ||
    partner.shares_contacts
  );
}

function filterPartners(partners: MockPartnerType[], params: URLSearchParams) {
  const search = params.get('partner_search')?.toLowerCase();
  const orgIds = params.getAll('partner_org_ids[]').map(Number);
  const tagIds = params.getAll('partner_tag_ids[]').map(Number);
  const populationIds = params.getAll('partner_population_ids[]').map(Number);
  const score = params.get('partner_score');
  const scoreBucket = params.get('partner_score_bucket');

  return partners.filter((partner) => {
    if (search && !partner.partner_name.toLowerCase().includes(search))
      return false;
    if (orgIds.length && !orgIds.includes(partner.partner_organization_id))
      return false;
    if (
      tagIds.length &&
      !partner.partner_tag_ids.some((id) => tagIds.includes(id))
    )
      return false;
    if (
      populationIds.length &&
      !partner.shared_with_us_population_ids.some((id) =>
        populationIds.includes(id),
      )
    )
      return false;
    if (score && partner.partner_score !== score) return false;
    if (scoreBucket && partner.partner_score_bucket !== scoreBucket)
      return false;
    return true;
  });
}

function buildPagination(
  url: URL,
  page: number,
  limit: number,
  totalCount: number,
): RecordPaginationType {
  const lastPage = Math.max(1, Math.ceil(totalCount / limit));
  const nextHref =
    page < lastPage ? `${url.pathname}?page=${page + 1}&limit=${limit}` : null;
  return {
    page,
    limit,
    last_page: lastPage,
    next_href: nextHref,
    total_count: totalCount,
  };
}

export function registerIndividualRecordMock() {
  worker.use(
    typedHttp.untyped.get(
      `${appConfig.apiBaseUrl}/v0.3/records/:sourceId/:sourcePrimaryKey`,
      ({ request }) => {
        const url = new URL(request.url);
        const params = url.searchParams;
        const section = (params.get('section') ??
          'all') as RecordSectionParamType;
        const page = Number(params.get('page') ?? 1);
        const limit = Number(params.get('limit') ?? 10);

        const eligible = filterPartners(mockPartners, params).filter(
          (partner) => sharesSection(partner, section),
        );
        const pageItems = eligible.slice((page - 1) * limit, page * limit);

        return HttpResponse.json({
          mdm_type: 'account',
          section,
          our_population_ids: [301, 302],
          own: buildOwn(section),
          partners: {
            items: pageItems.map((partner) => buildPartner(partner, section)),
            pagination: buildPagination(url, page, limit, eligible.length),
          },
        });
      },
    ),
  );
}
